<template>
  <div>
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mt-5 pt-5 ps-3">
      <ol class="breadcrumb mb-3">
        <li class="breadcrumb-item">
          <router-link to="/">Home</router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link to="/admin/agents">Agents</router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">
          {{ isEditMode ? "Edit" : "New" }}
        </li>
      </ol>
    </nav>

    <div class="container-fluid">
      <div class="row">
        <div class="col-lg-10 col-md-12 mx-auto">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-1">
                {{ isEditMode ? "Edit Agent" : "Create Agent" }}
              </h5>
              <small class="text-muted">
                {{
                  isEditMode
                    ? "Update agent configuration and settings"
                    : "Create a new AI agent with custom configuration"
                }}
              </small>
            </div>

            <div class="card-body">
              <!-- Loading State -->
              <div v-if="isLoading" class="text-center py-4">
                <div class="spinner-border loading-spinner text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2">Loading agent...</p>
              </div>

              <!-- Form -->
              <form v-else @submit.prevent="handleSubmit">
                <!-- Basic Information Section -->
                <div class="mb-4">
                  <h6 class="text-primary mb-3">
                    <i class="fa fa-info-circle me-2"></i>Basic Information
                  </h6>

                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label for="name" class="form-label">
                        Name <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        class="form-control"
                        id="name"
                        v-model="formData.name"
                        :class="{ 'is-invalid': errors.name }"
                        placeholder="e.g., Customer Support Agent"
                        required
                      />
                      <div v-if="errors.name" class="invalid-feedback">
                        {{ errors.name }}
                      </div>
                    </div>

                    <div class="col-md-6 mb-3">
                      <label for="slug" class="form-label">
                        Slug <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        class="form-control"
                        id="slug"
                        v-model="formData.slug"
                        :class="{ 'is-invalid': errors.slug }"
                        placeholder="e.g., customer-support"
                        required
                      />
                      <div v-if="errors.slug" class="invalid-feedback">
                        {{ errors.slug }}
                      </div>
                      <small class="form-text text-muted">
                        Unique identifier (lowercase, hyphens allowed)
                      </small>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="description" class="form-label">
                      Description
                    </label>
                    <textarea
                      class="form-control"
                      id="description"
                      v-model="formData.description"
                      :class="{ 'is-invalid': errors.description }"
                      rows="3"
                      placeholder="Describe the agent's purpose and capabilities"
                    ></textarea>
                    <div v-if="errors.description" class="invalid-feedback">
                      {{ errors.description }}
                    </div>
                  </div>
                </div>

                <!-- System Prompt Section -->
                <div class="mb-4">
                  <h6 class="text-primary mb-3">
                    <i class="fa fa-code me-2"></i>System Prompt
                  </h6>

                  <div class="mb-3">
                    <label for="systemPrompt" class="form-label">
                      System Prompt <span class="text-danger">*</span>
                    </label>
                    <textarea
                      class="form-control font-monospace"
                      id="systemPrompt"
                      v-model="formData.systemPrompt"
                      :class="{ 'is-invalid': errors.systemPrompt }"
                      rows="8"
                      placeholder="You are a helpful assistant that..."
                      required
                    ></textarea>
                    <div v-if="errors.systemPrompt" class="invalid-feedback">
                      {{ errors.systemPrompt }}
                    </div>
                    <small class="form-text text-muted">
                      Define the agent's behavior, personality, and instructions
                    </small>
                  </div>
                </div>

                <!-- Model Configuration Section -->
                <div class="mb-4">
                  <h6 class="text-primary mb-3">
                    <i class="fa fa-cog me-2"></i>Model Configuration
                  </h6>

                  <div class="row">
                    <div class="col-md-4 mb-3">
                      <label for="model" class="form-label">
                        Model <span class="text-danger">*</span>
                      </label>
                      <select
                        class="form-select"
                        id="model"
                        v-model="formData.model"
                        :class="{ 'is-invalid': errors.model }"
                        required
                      >
                        <option value="">Select Model</option>
                        <option value="gpt-4">GPT-4</option>
                        <option value="gpt-4-turbo">GPT-4 Turbo</option>
                        <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                        <option value="claude-3-opus">Claude 3 Opus</option>
                        <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                        <option value="claude-3-haiku">Claude 3 Haiku</option>
                      </select>
                      <div v-if="errors.model" class="invalid-feedback">
                        {{ errors.model }}
                      </div>
                    </div>

                    <div class="col-md-4 mb-3">
                      <label for="temperature" class="form-label">
                        Temperature
                      </label>
                      <input
                        type="number"
                        class="form-control"
                        id="temperature"
                        v-model.number="formData.temperature"
                        :class="{ 'is-invalid': errors.temperature }"
                        min="0"
                        max="2"
                        step="0.1"
                        placeholder="0.7"
                      />
                      <div v-if="errors.temperature" class="invalid-feedback">
                        {{ errors.temperature }}
                      </div>
                      <small class="form-text text-muted">
                        0 = deterministic, 2 = creative
                      </small>
                    </div>

                    <div class="col-md-4 mb-3">
                      <label for="maxOutputTokens" class="form-label">
                        Max Output Tokens
                      </label>
                      <input
                        type="number"
                        class="form-control"
                        id="maxOutputTokens"
                        v-model.number="formData.maxOutputTokens"
                        :class="{ 'is-invalid': errors.maxOutputTokens }"
                        min="1"
                        max="4096"
                        placeholder="2000"
                      />
                      <div
                        v-if="errors.maxOutputTokens"
                        class="invalid-feedback"
                      >
                        {{ errors.maxOutputTokens }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tools & Capabilities Section -->
                <div class="mb-4">
                  <h6 class="text-primary mb-3">
                    <i class="fa fa-wrench me-2"></i>Tools & Capabilities
                  </h6>

                  <div class="mb-3">
                    <label class="form-label">Tools Allowed</label>
                    <div class="row">
                      <div
                        v-for="tool in availableTools"
                        :key="tool.value"
                        class="col-md-4 mb-2"
                      >
                        <div class="form-check">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`tool-${tool.value}`"
                            :value="tool.value"
                            v-model="formData.toolsAllowed"
                          />
                          <label
                            class="form-check-label"
                            :for="`tool-${tool.value}`"
                          >
                            {{ tool.label }}
                          </label>
                        </div>
                      </div>
                    </div>
                    <small class="form-text text-muted">
                      Select which tools this agent can use
                    </small>
                  </div>

                  <div class="mb-3">
                    <label for="knowledgeBaseIds" class="form-label fw-bold">
                      Knowledge Bases
                    </label>
                    <VueMultiselect
                      v-model="formData.knowledgeBaseIds"
                      :options="knowledgeBases"
                      :multiple="true"
                      :close-on-select="false"
                      :clear-on-select="false"
                      :preserve-search="true"
                      placeholder="Select Knowledge Bases"
                      label="name"
                      track-by="_id"
                      :preselect-first="false"
                    >
                      <template #noResult>
                        <span>No knowledge bases found.</span>
                      </template>
                    </VueMultiselect>
                    <small class="form-text text-muted mt-1 d-block">
                      Select one or more knowledge bases for this agent
                    </small>
                  </div>
                </div>

                <!-- Meta Options Section -->
                <div class="mb-4">
                  <h6 class="text-primary mb-3">
                    <i class="fa fa-tags me-2"></i>Agent Meta Information
                  </h6>
                  
                  <ul class="nav nav-tabs mb-3" id="metaTabs" role="tablist">
                    <li class="nav-item" role="presentation" v-for="tab in metaTabs" :key="tab.id">
                      <button
                        class="nav-link text-capitalize"
                        :class="{ active: activeTab === tab.id }"
                        :id="tab.id + '-tab'"
                        type="button"
                        role="tab"
                        @click="activeTab = tab.id"
                      >
                        {{ tab.label }}
                      </button>
                    </li>
                  </ul>

                  <div class="tab-content border rounded p-3" style="max-height: 300px; overflow-y: auto; background-color: #f8f9fa;">
                    <div v-if="isMetaLoading" class="text-center py-3">
                      <div class="spinner-border loading-spinner text-primary" role="status"></div>
                      <span class="ms-2 small">Loading options...</span>
                    </div>
                    <div v-else-if="metaOptions[activeTab].length === 0" class="text-center py-3 text-muted">
                      No options available for {{ activeTab }}
                    </div>
                    <div v-else class="row g-2">
                      <div 
                        v-for="option in metaOptions[activeTab]" 
                        :key="option._id || option.id || option" 
                        class="col-md-4 col-sm-6"
                      >
                        <div class="form-check">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="option._id || option.id || option"
                            :value="option.title || option.name || option.label || option"
                            v-model="formData[activeTab]"
                          />
                          <label class="form-check-label small" :for="option._id || option.id || option">
                            {{ option.title || option.name || option.label || option }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <small class="form-text text-muted mt-2 d-block">
                    Select multiple options relevant to this agent
                  </small>
                </div>

                <!-- Status Section -->
                <div class="mb-4">
                  <h6 class="text-primary mb-3">
                    <i class="fa fa-toggle-on me-2"></i>Status
                  </h6>

                  <div class="form-check form-switch px-0">
                    <input
                      class="form-check-input ms-1"
                      type="checkbox"
                      id="isActive"
                      v-model="formData.isActive"
                    />
                    <label class="form-check-label ps-2" for="isActive">
                      Active
                    </label>
                  </div>
                  <small class="form-text text-muted">
                    Only active agents can be used in conversations
                  </small>
                </div>

                <!-- Error Message -->
                <div v-if="submitError" class="alert alert-danger" role="alert">
                  <i class="fa fa-exclamation-triangle me-2"></i>
                  {{ submitError }}
                </div>

                <!-- Action Buttons -->
                <div class="d-flex gap-2 justify-content-end">
                  <router-link
                    to="/admin/agents"
                    class="btn btn-outline-secondary"
                  >
                    <i class="fa fa-times me-1"></i>
                    Cancel
                  </router-link>
                  <button
                    type="submit"
                    class="btn btn-primary"
                    :disabled="isSaving"
                  >
                    <span v-if="isSaving">
                      <span
                        class="spinner-border spinner-border-sm me-1"
                        role="status"
                      ></span>
                      Saving...
                    </span>
                    <span v-else>
                      <i class="fa fa-save me-1"></i>
                      {{ isEditMode ? "Update" : "Create" }}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import apiService from "@/services/apiService";
import VueMultiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";

// Router
const router = useRouter();
const route = useRoute();

// Computed
const isEditMode = computed(() => !!route.params.id);
const agentId = computed(() => route.params.id as string);

type MetaKey =
  | "capabilities"
  | "roles"
  | "competencies"
  | "rules"
  | "skills"
  | "levels";

// Available tools
const availableTools = ref([
  { value: "web_search", label: "Web Search" },
  { value: "calculator", label: "Calculator" },
  { value: "code_interpreter", label: "Code Interpreter" },
  { value: "image_generation", label: "Image Generation" },
  { value: "file_upload", label: "File Upload" },
  { value: "knowledge_base", label: "Knowledge Base" },
]);

// Knowledge bases
const knowledgeBases = ref<any[]>([]);

// Reactive data
const formData = reactive({
  name: "",
  slug: "",
  description: "",
  systemPrompt: "",
  model: "",
  temperature: 0.7,
  maxOutputTokens: 2000,
  toolsAllowed: [] as string[],
  knowledgeBaseIds: [] as string[],
  isActive: true,
  capabilities: [] as string[],
  roles: [] as string[],
  competencies: [] as string[],
  rules: [] as string[],
  skills: [] as string[],
  levels: [] as string[],
});

const metaTabs: { id: MetaKey; label: string }[] = [
  { id: "capabilities", label: "Capabilities" },
  { id: "roles", label: "Roles" },
  { id: "competencies", label: "Competencies" },
  { id: "rules", label: "Rules" },
  { id: "skills", label: "Skills" },
  { id: "levels", label: "Levels" },
];

const metaOptions = reactive<Record<MetaKey, any[]>>({
  capabilities: [],
  roles: [],
  competencies: [],
  rules: [],
  skills: [],
  levels: [],
});

const activeTab = ref<MetaKey>("capabilities");
const isMetaLoading = ref(false);

const errors = reactive<Record<string, string>>({});
const isLoading = ref(false);
const isSaving = ref(false);
const submitError = ref<string | null>(null);

// Track if slug was manually edited
const slugManuallyEdited = ref(false);

// Methods
const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "") // Remove special characters
    .replace(/[\s_]+/g, "-") // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
};

// Watch name changes to auto-generate slug (only when creating new agent)
watch(
  () => formData.name,
  (newName) => {
    // Only auto-generate slug if not in edit mode and slug hasn't been manually edited
    if (!isEditMode.value && !slugManuallyEdited.value) {
      formData.slug = generateSlug(newName);
    }
  }
);

// Watch slug changes to detect manual edits
watch(
  () => formData.slug,
  (newSlug, oldSlug) => {
    // If slug is manually changed (not empty and different from auto-generated)
    if (newSlug && oldSlug !== undefined) {
      const autoSlug = generateSlug(formData.name);
      if (newSlug !== autoSlug) {
        slugManuallyEdited.value = true;
      }
    }
  }
);

const loadKnowledgeBases = async () => {
  try {
    const response = await apiService.getKnowledgeBases({ isActive: true });
    if (response) {
      knowledgeBases.value = response.data || response.knowledgeBases || [];
    }
  } catch (err: any) {
    console.error("Error loading knowledge bases:", err);
  }
};

const loadAgent = async () => {
  if (!isEditMode.value) return;

  isLoading.value = true;
  try {
    const response = await apiService.getAgentById(agentId.value);

    // Populate form with existing data
    if (response) {
      formData.name = response.name || "";
      formData.slug = response.slug || "";
      formData.description = response.description || "";
      formData.systemPrompt =
        response.system_prompt || response.systemPrompt || "";
      formData.model = response.model || "";
      formData.temperature = response.temperature ?? 0.7;
      formData.maxOutputTokens =
        response.max_output_tokens ?? response.maxOutputTokens ?? 2000;
      formData.toolsAllowed =
        response.tools_allowed || response.toolsAllowed || [];
      formData.knowledgeBaseIds = (response.knowledge_base_ids || response.knowledgeBaseIds || []).map((id: string) => {
        const kb = knowledgeBases.value.find(k => (k._id || k.id) === id);
        return kb || id;
      });
      formData.isActive = response.is_active ?? response.isActive ?? true;
      formData.capabilities = response.capabilities || [];
      formData.roles = response.roles || [];
      formData.competencies = response.competencies || [];
      formData.rules = response.rules || [];
      formData.skills = response.skills || [];
      formData.levels = response.levels || [];
    }
  } catch (err: any) {
    console.error("Error loading agent:", err);
    showToast(err?.response?.data?.message || "Failed to load agent", "error");
    router.push("/admin/agents");
    } finally {
    isLoading.value = false;
  }
};

const fetchMetaOptions = async () => {
  isMetaLoading.value = true;
  try {
    const [
      capabilities,
      roles,
      competencies,
      rules,
      skills,
      levels,
    ] = await Promise.all([
      apiService.getAgentCapabilities(),
      apiService.getAgentRoles(),
      apiService.getAgentCompetencies(),
      apiService.getAgentRules(),
      apiService.getAgentSkills(),
      apiService.getAgentLevels(),
    ]);

    metaOptions.capabilities = capabilities?.data || capabilities || [];
    metaOptions.roles = roles?.data || roles || [];
    metaOptions.competencies = competencies?.data || competencies || [];
    metaOptions.rules = rules?.data || rules || [];
    metaOptions.skills = skills?.data || skills || [];
    metaOptions.levels = levels?.data || levels || [];
  } catch (err) {
    console.error("Error fetching meta options:", err);
  } finally {
    isMetaLoading.value = false;
  }
};

const validateForm = (): boolean => {
  // Clear previous errors
  Object.keys(errors).forEach((key) => delete errors[key]);

  let isValid = true;

  // Validate name
  if (!formData.name.trim()) {
    errors.name = "Name is required";
    isValid = false;
  } else if (formData.name.length < 3) {
    errors.name = "Name must be at least 3 characters";
    isValid = false;
  }

  // Validate slug
  if (!formData.slug.trim()) {
    errors.slug = "Slug is required";
    isValid = false;
  } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
    errors.slug =
      "Slug must contain only lowercase letters, numbers, and hyphens";
    isValid = false;
  }

  // Validate system prompt
  if (!formData.systemPrompt || !formData.systemPrompt.trim()) {
    errors.systemPrompt = "System prompt is required";
    isValid = false;
  } else if (formData.systemPrompt.trim().length < 10) {
    errors.systemPrompt = "System prompt must be at least 10 characters";
    isValid = false;
  }

  // Validate model
  if (!formData.model) {
    errors.model = "Model is required";
    isValid = false;
  }

  // Validate temperature
  if (formData.temperature < 0 || formData.temperature > 2) {
    errors.temperature = "Temperature must be between 0 and 2";
    isValid = false;
  }

  // Validate maxOutputTokens
  if (
    formData.maxOutputTokens &&
    (formData.maxOutputTokens < 1 || formData.maxOutputTokens > 4096)
  ) {
    errors.maxOutputTokens = "Max output tokens must be between 1 and 4096";
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async () => {
  submitError.value = null;

  // Validate form
  if (!validateForm()) {
    return;
  }

  isSaving.value = true;

  try {
    const payload = {
      name: formData.name.trim(),
      slug: formData.slug.trim(),
      description: formData.description.trim() || undefined,
      system_prompt: formData.systemPrompt.trim(),
      model: formData.model,
      temperature: formData.temperature,
      max_output_tokens: formData.maxOutputTokens,
      tools_allowed: formData.toolsAllowed,
      knowledge_base_ids: formData.knowledgeBaseIds.map((kb: any) => typeof kb === 'object' ? (kb._id || kb.id) : kb),
      is_active: formData.isActive,
      capabilities: formData.capabilities,
      roles: formData.roles,
      competencies: formData.competencies,
      rules: formData.rules,
      skills: formData.skills,
      levels: formData.levels,
    };

    // Debug logging
    console.log("Submitting agent payload:", payload);
    console.log("System prompt length:", payload.system_prompt.length);

    if (isEditMode.value) {
      // Update existing agent
      await apiService.updateAgent(agentId.value, payload);
      showToast("Agent updated successfully", "success");
      router.push(`/admin/agents/${agentId.value}`);
    } else {
      // Create new agent
      const response = await apiService.createAgent(payload);
      showToast("Agent created successfully", "success");

      // Redirect to the new agent details page
      if (response._id || response.id) {
        router.push(`/admin/agents/${response._id || response.id}`);
      } else {
        router.push("/admin/agents");
      }
    }
  } catch (err: any) {
    console.error("Error saving agent:", err);
    const errorMessage =
      err?.response?.data?.message || err?.message || "Failed to save agent";
    submitError.value = errorMessage;
    showToast(errorMessage, "error");
  } finally {
    isSaving.value = false;
  }
};

const showToast = (message: string, type: "success" | "error" | "info") => {
  console.log(`[${type.toUpperCase()}] ${message}`);
  // Integrate with your toast library here
};

// Lifecycle
onMounted(() => {
  loadKnowledgeBases();
  fetchMetaOptions();
  if (isEditMode.value) {
    loadAgent();
  }
});
</script>

<style scoped>
.breadcrumb-item a {
  text-decoration: none;
  color: #6c757d;
}

.breadcrumb-item a:hover {
  color: #495057;
}

.font-monospace {
  font-family: "Courier New", Courier, monospace;
  font-size: 0.9rem;
}

.form-check-input {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
}

.form-control.is-invalid,
.form-select.is-invalid {
  border-color: #dc3545;
}

h6.text-primary {
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.loading-spinner {
  width: 30px;
  height: 30px;
}

/* Custom styles for meta tabs */
.nav-tabs .nav-link {
  border: none;
  color: #6c757d;
  font-weight: 500;
  padding: 0.75rem 1.25rem;
  transition: all 0.2s ease;
  border-bottom: 2px solid transparent;
}

.nav-tabs .nav-link:hover {
  color: #35bfbf;
  background-color: transparent;
  border-color: transparent;
}

.nav-tabs .nav-link.active {
  color: #35bfbf;
  background-color: transparent;
  border-color: transparent;
  border-bottom: 2px solid #35bfbf;
}

.tab-content::-webkit-scrollbar {
  width: 6px;
}

.tab-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.tab-content::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 10px;
}

.tab-content::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.form-check {
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

/* .form-check:hover {
  background-color: #e9ecef;
} */

.form-check-input:checked + .form-check-label {
  color: #35bfbf;
  font-weight: 600;
}

/* VueMultiselect Premium Styling */
:deep(.multiselect__tags) {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 8px 40px 0 8px;
}

:deep(.multiselect__tag) {
  background: #35bfbf;
}

:deep(.multiselect__tag-icon:after) {
  color: #fff;
}

:deep(.multiselect__tag-icon:focus, .multiselect__tag-icon:hover) {
  background: #2a9b9b;
}

:deep(.multiselect__option--highlight) {
  background: #35bfbf;
}

:deep(.multiselect__option--highlight:after) {
  background: #35bfbf;
}
</style>
